// =====================================================================
// paginas/cadastro.js — Inquilinos e candidatos.
//
// O candidato chega pelo subapp público (com consentimento LGPD) e cai
// aqui como 'pre_cadastro'. A equipe analisa, registra a consulta de
// crédito e aprova ou reprova.
//
// Duas coisas que o sistema NÃO deixa a pessoa fazer, de propósito:
//   - aprovar sem análise de crédito registrada (some a trilha);
//   - gerar contrato sem os fiadores que a regra exige (trigger no banco).
// =====================================================================

import { sb } from '../../../../packages/ui/cliente.js';
import { icon, escapeHtml, tabela, badge, modal, cabecalho, checar, confirmar } from '../ui.js';
import { moeda, data, cpf as fmtCpf, telefone as fmtTel } from '../../../../packages/ui/format.js';

const STATUS = {
  pre_cadastro: ['Pré-cadastro', 'azul'],
  em_analise:   ['Em análise',   'ambar'],
  aprovado:     ['Aprovado',     'verde'],
  reprovado:    ['Reprovado',    'vermelho'],
  ativo:        ['Inquilino',    'verde'],
  encerrado:    ['Encerrado',    'cinza']
};

let filtro = '';

export async function pagCadastro(el, estado) {
  const lista = checar(await sb
    .from('inquilinos')
    .select('id,nome,cpf,email,telefone,status,renda_mensal,criado_em,anonimizado_em,'
          + 'analises_credito(resultado,consultado_em)')
    .order('criado_em', { ascending: false }));

  el.innerHTML = '';
  el.appendChild(cabecalho({
    titulo: 'Cadastro de inquilinos',
    busca: 'Buscar por nome ou CPF…',
    onBusca: (v) => { filtro = v; desenhar(); },
    acao: 'Novo inquilino',
    onAcao: () => formulario(null, () => pagCadastro(el, estado))
  }));

  const corpo = document.createElement('div');
  el.appendChild(corpo);

  const pendentes = lista.filter(i => ['pre_cadastro', 'em_analise'].includes(i.status));
  if (pendentes.length) {
    const aviso = document.createElement('div');
    aviso.className = 'aviso atencao';
    aviso.innerHTML = `${icon('alerta')} <div><strong>${pendentes.length} candidato(s) aguardando análise.</strong>
      Registre a consulta de crédito antes de aprovar — sem ela, o sistema exige 2 fiadores.</div>`;
    corpo.appendChild(aviso);
  }

  function desenhar() {
    const f = lista.filter(i =>
      !filtro || i.nome.toLowerCase().includes(filtro) || (i.cpf || '').includes(filtro));

    const tab = document.createElement('div');
    tab.innerHTML = tabela([
      { th: 'Nome', td: i => `<strong>${escapeHtml(i.nome)}</strong>` },
      { th: 'CPF',  td: i => escapeHtml(fmtCpf(i.cpf)) },
      { th: 'Telefone', td: i => escapeHtml(fmtTel(i.telefone)) },
      { th: 'Renda', cls: 'num', td: i => i.renda_mensal ? moeda(i.renda_mensal) : '—' },
      { th: 'Crédito', td: i => {
          const a = (i.analises_credito || [])[0];
          if (!a) return badge('sem consulta', 'cinza');
          const cor = a.resultado === 'limpo' ? 'verde' : a.resultado === 'restricao' ? 'vermelho' : 'ambar';
          return badge(a.resultado, cor);
        } },
      { th: 'Situação', td: i => {
          const [txt, cor] = STATUS[i.status] || [i.status, 'cinza'];
          return badge(txt, cor);
        } },
      { th: '', cls: 'acoes', td: i => `
          <button class="icone" data-credito="${i.id}" title="Registrar consulta de crédito">${icon('seguranca')}</button>
          <button class="icone" data-editar="${i.id}" title="Editar">${icon('editar')}</button>
          ${['pre_cadastro', 'em_analise'].includes(i.status)
            ? `<button class="link" data-aprovar="${i.id}">Aprovar</button>
               <button class="link perigo" data-reprovar="${i.id}">Reprovar</button>` : ''}` }
    ], f, 'Nenhum inquilino cadastrado.');

    tab.querySelectorAll('[data-editar]').forEach(b => b.onclick = () =>
      formulario(lista.find(i => i.id === b.dataset.editar), () => pagCadastro(el, estado)));

    tab.querySelectorAll('[data-credito]').forEach(b => b.onclick = () =>
      formCredito(b.dataset.credito, estado, () => pagCadastro(el, estado)));

    tab.querySelectorAll('[data-aprovar]').forEach(b => b.onclick = async () => {
      const id = b.dataset.aprovar;
      const { data: a } = await sb.from('analises_credito')
        .select('id').eq('inquilino_id', id).limit(1);
      if (!a?.length && !confirmar(
        'Este candidato não tem consulta de crédito registrada.\n\n'
        + 'Sem ela, o sistema vai exigir 2 fiadores no contrato. Aprovar mesmo assim?')) return;
      checar(await sb.from('inquilinos').update({ status: 'aprovado' }).eq('id', id));
      pagCadastro(el, estado);
    });

    tab.querySelectorAll('[data-reprovar]').forEach(b => b.onclick = async () => {
      const motivo = prompt('Motivo da reprovação:');
      if (motivo === null) return;
      // Descarte LGPD: marca a data em que os dados serão anonimizados.
      const { data: t } = await sb.from('tenants')
        .select('dias_retencao_candidato').eq('id', estado.perfil.tenant_id).maybeSingle();
      const dias = (t && t.dias_retencao_candidato) || 90;
      const descartar = new Date(Date.now() + dias * 864e5).toISOString().slice(0, 10);

      checar(await sb.from('inquilinos').update({
        status: 'reprovado',
        motivo_reprovacao: motivo,
        reprovado_em: new Date().toISOString().slice(0, 10),
        descartar_em: descartar
      }).eq('id', b.dataset.reprovar));

      alert(`Reprovado. Os dados pessoais serão anonimizados automaticamente em ${dias} dias `
          + `(exigência da LGPD — não há base legal para guardar CPF e comprovante de renda `
          + `de quem não virou cliente).`);
      pagCadastro(el, estado);
    });

    corpo.querySelector('table, .vazio')?.remove();
    corpo.appendChild(tab.firstElementChild);
  }

  desenhar();
}

// ─── Formulário do inquilino ──────────────────────────────────────────
function formulario(inq, aoSalvar) {
  const campos = [
    { nome: 'nome', label: 'Nome completo', req: true, full: true },
    { nome: 'cpf', label: 'CPF', req: true },
    { nome: 'rg', label: 'Identidade (RG/CNH)' },
    { nome: 'data_nascimento', label: 'Data de nascimento', tipo: 'date' },
    { nome: 'telefone', label: 'Telefone com DDD', req: true },
    { nome: 'email', label: 'E-mail', tipo: 'email' },
    { nome: 'profissao', label: 'Profissão' },
    { nome: 'renda_mensal', label: 'Renda mensal', tipo: 'number', step: '0.01' },
    { nome: 'estado_civil', label: 'Estado civil', tipo: 'select',
      opcoes: ['', 'solteiro', 'casado', 'divorciado', 'viuvo', 'uniao_estavel'] },
    { nome: 'regime_bens', label: 'Regime de bens (se casado)' },
    { nome: 'conjuge_nome', label: 'Cônjuge — nome', full: true },
    { nome: 'conjuge_cpf', label: 'Cônjuge — CPF' },
    { nome: 'conjuge_rg', label: 'Cônjuge — identidade' },
    { nome: 'conjuge_telefone', label: 'Cônjuge — telefone' },
    { nome: 'endereco_atual', label: 'Endereço atual', tipo: 'textarea', full: true }
  ];

  modal({
    titulo: inq ? `Editar — ${inq.nome}` : 'Novo inquilino',
    campos,
    dados: inq || {},
    aviso: inq ? null
      : 'Cadastro feito aqui não gera consentimento LGPD do titular. Se o candidato pode preencher '
      + 'sozinho, prefira mandar o link do portal — lá o consentimento fica registrado.',
    onSalvar: async (d) => {
      d.cpf = (d.cpf || '').replace(/\D/g, '');
      d.telefone = (d.telefone || '').replace(/\D/g, '');
      d.renda_mensal = d.renda_mensal ? Number(d.renda_mensal) : null;

      if (inq) checar(await sb.from('inquilinos').update(d).eq('id', inq.id));
      else     checar(await sb.from('inquilinos').insert({ ...d, status: 'em_analise' }));
      aoSalvar();
    }
  });
}

// ─── Consulta de crédito ──────────────────────────────────────────────
function formCredito(inquilinoId, estado, aoSalvar) {
  modal({
    titulo: 'Registrar consulta de crédito',
    aviso: 'A consulta é feita no site do bureau. Aqui você registra o resultado e anexa o PDF. '
         + 'O sistema aplica a regra sozinho: nome limpo exige 1 fiador; com restrição, 2.',
    campos: [
      { nome: 'bureau', label: 'Bureau', tipo: 'select',
        opcoes: [{ valor: 'serasa', texto: 'Serasa' }, { valor: 'spc', texto: 'SPC' },
                 { valor: 'boa_vista', texto: 'Boa Vista' }, { valor: 'outro', texto: 'Outro' }] },
      { nome: 'resultado', label: 'Resultado', tipo: 'select', req: true,
        opcoes: [{ valor: 'limpo', texto: 'Nome limpo' },
                 { valor: 'restricao', texto: 'Com restrição' },
                 { valor: 'inconclusivo', texto: 'Inconclusivo' }] },
      { nome: 'score', label: 'Score (0-1000)', tipo: 'number' },
      { nome: 'observacao', label: 'Observação', tipo: 'textarea', full: true }
    ],
    onSalvar: async (d) => {
      checar(await sb.from('analises_credito').insert({
        tenant_id: estado.perfil.tenant_id,
        inquilino_id: inquilinoId,
        bureau: d.bureau || 'serasa',
        resultado: d.resultado,
        score: d.score ? Number(d.score) : null,
        observacao: d.observacao,
        consultado_por: estado.perfil.id,
        finalidade: 'analise_locacao'
      }));
      aoSalvar();
    }
  });
}
